import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/core/ApiError';
import { ApiResponseHandlerType } from './ApiResponseHandlerContext';
import { useApiResponseHandlerContext } from './useApiResponseHandlerContext';

type OnErrorMessageGenerator = (error: ApiError) => string;
type ShowNotification = (message: string, type: ApiResponseHandlerType) => void;
type FinallyCallback = () => void;
type SafeExecuteParams<T> = {
  apiCall: () => Promise<T>;
  successMessage: string;
  onErrorMessageGenerator?: OnErrorMessageGenerator;
  finallyCallback?: FinallyCallback;
};
const createSafeExecute = (showNotification: ShowNotification, t: (key: string) => string) => {
  return async <T>({
    apiCall,
    successMessage,
    onErrorMessageGenerator,
    finallyCallback,
  }: SafeExecuteParams<T>): Promise<T> => {
    try {
      const result = await apiCall();
      showNotification(successMessage, ApiResponseHandlerType.SUCCESS);
      return result;
    } catch (error) {
      if (error instanceof ApiError) {
        const message = onErrorMessageGenerator
          ? onErrorMessageGenerator(error)
          : error.body?.message || t('api.error.default');
        showNotification(message, ApiResponseHandlerType.ERROR);
      } else {
        console.error('An unexpected api error occurred:', error);
        showNotification('An unexpected error occurred', ApiResponseHandlerType.ERROR);
      }
      throw error;
    } finally {
      if (finallyCallback) {
        finallyCallback();
      }
    }
  };
};

export const useSafeExecute = () => {
  const { t } = useTranslation();

  const { showResponseNotification } = useApiResponseHandlerContext();
  return createSafeExecute(showResponseNotification, t);
};
